import {
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  PauseCircle,
  Plus,
  RotateCcw,
  XCircle,
} from 'lucide-react'
import { useMemo, useState } from 'react'

import { Badge } from '@/components/admin/shared/badge'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import { DiffMarker } from './diff-marker'
import { useRecommendationEngineMock } from './recommendation-engine-context'
import type { RecommendationItem, RecommendationStatus } from './types'

const STATUS_META: Record<
  RecommendationStatus,
  { label: string; variant: string }
> = {
  suggested: { label: 'Suggested', variant: 'topic' },
  assigned: { label: 'Assigned', variant: 'active' },
  postponed: { label: 'Postponed', variant: 'paused' },
  rejected: { label: 'Rejected', variant: 'archived' },
}

function TrackBadge({ track }: { track: RecommendationItem['track'] }) {
  return (
    <Badge
      variant={track === 'baseline' ? 'default' : 'premium'}
      className="uppercase tracking-wide"
    >
      {track === 'baseline' ? 'Baseline track' : 'Adjustment'}
    </Badge>
  )
}

function RecommendationCard({
  rec,
  onStatusChange,
  statusDiff,
}: {
  rec: RecommendationItem
  onStatusChange: (id: string, status: RecommendationStatus) => void
  statusDiff?: { previousValue: string | number; currentValue: string | number }
}) {
  const [open, setOpen] = useState(rec.rank <= 2)
  const statusMeta = STATUS_META[rec.status]

  return (
    <div className="border-border overflow-hidden rounded-lg border">
      <div className="flex items-start gap-3 p-3">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="text-muted-foreground hover:text-foreground mt-0.5 shrink-0"
          aria-label={open ? 'Collapse rationale' : 'Expand rationale'}
        >
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-muted-foreground text-xs font-semibold">
              #{rec.rank}
            </span>
            <span className="text-foreground text-sm font-medium">
              {rec.problemTitle}
            </span>
            <Badge variant={rec.difficulty}>{rec.difficulty}</Badge>
            <span className="text-muted-foreground text-xs">{rec.source}</span>
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            <Badge variant="topic">{rec.topicName}</Badge>
            <span className="text-muted-foreground text-xs">
              → {rec.subtopicName}
            </span>
            <TrackBadge track={rec.track} />
            <Badge variant={statusMeta.variant}>{statusMeta.label}</Badge>
            {statusDiff && (
              <DiffMarker
                previousValue={statusDiff.previousValue}
                currentValue={statusDiff.currentValue}
              />
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="flex shrink-0 items-center gap-1">
          {rec.status === 'suggested' ? (
            <>
              <button
                type="button"
                onClick={() => onStatusChange(rec.id, 'assigned')}
                className="bg-primary text-primary-foreground flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium transition-opacity hover:opacity-90"
              >
                <Plus size={13} />
                Assign
              </button>
              <button
                type="button"
                onClick={() => onStatusChange(rec.id, 'postponed')}
                className="text-muted-foreground hover:text-foreground hover:bg-muted rounded-md p-1.5 transition-colors"
                aria-label="Postpone"
                title="Postpone"
              >
                <PauseCircle size={15} />
              </button>
              <button
                type="button"
                onClick={() => onStatusChange(rec.id, 'rejected')}
                className="text-muted-foreground hover:bg-muted rounded-md p-1.5 transition-colors hover:text-rose-500"
                aria-label="Dismiss"
                title="Dismiss"
              >
                <XCircle size={15} />
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => onStatusChange(rec.id, 'suggested')}
              className="text-muted-foreground hover:text-foreground hover:bg-muted flex items-center gap-1 rounded-md px-2 py-1 text-xs transition-colors"
            >
              <RotateCcw size={13} />
              Revert
            </button>
          )}
        </div>
      </div>

      {open && (
        <div className="border-border bg-muted/20 grid gap-4 border-t p-3 md:grid-cols-[1.4fr_1fr]">
          {/* Reasons */}
          <div>
            <p className="text-foreground mb-2 text-xs font-semibold">
              Why this is recommended
            </p>
            <ul className="space-y-2">
              {rec.reasons.map((reason) => (
                <li key={reason.label} className="flex gap-2">
                  <span className="bg-primary mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" />
                  <span className="text-xs">
                    <span className="text-foreground font-medium">
                      {reason.label}.
                    </span>{' '}
                    <span className="text-muted-foreground">
                      {reason.detail}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </div>

          {/* Scoreboard */}
          <div>
            <p className="text-foreground mb-2 text-xs font-semibold">
              Scoring
            </p>
            <div className="space-y-2">
              {rec.scoreboard.map((entry) => (
                <Progress
                  key={entry.label}
                  value={(entry.value / entry.max) * 100}
                  label={entry.label}
                  size="sm"
                  barClassName={
                    entry.label === 'Priority score'
                      ? 'bg-primary'
                      : 'bg-slate-400 dark:bg-slate-500'
                  }
                />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/**
 * Recommendations view — the ranked list of suggested problems with rationale,
 * baseline/adjustment provenance, and coach actions (assign / postpone /
 * dismiss). The History toggle exposes the full lifecycle so there is no
 * mystery behind why a problem is or is not currently recommended.
 */
export function RecommendationsView() {
  const { pendingDiff, recommendations, setRecommendationStatus, topics } =
    useRecommendationEngineMock()
  const [showHistory, setShowHistory] = useState(false)
  const [topicFilter, setTopicFilter] = useState<string>('')
  const [trackFilter, setTrackFilter] = useState<string>('')

  const visible = useMemo(() => {
    return recommendations.filter((r) => {
      if (!showHistory && r.status !== 'suggested') return false
      if (topicFilter && r.topicId !== topicFilter) return false
      if (trackFilter && r.track !== trackFilter) return false
      return true
    })
  }, [recommendations, showHistory, topicFilter, trackFilter])

  const suggestedCount = recommendations.filter(
    (r) => r.status === 'suggested',
  ).length

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="bg-muted/50 flex gap-1 rounded-lg p-1">
          <button
            type="button"
            onClick={() => setShowHistory(false)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              !showHistory
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            Active ({suggestedCount})
          </button>
          <button
            type="button"
            onClick={() => setShowHistory(true)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              showHistory
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            History ({recommendations.length})
          </button>
        </div>

        <select
          value={topicFilter}
          onChange={(e) => setTopicFilter(e.target.value)}
          className="bg-muted border-border text-foreground rounded-lg border px-2.5 py-1.5 text-sm"
          aria-label="Filter by topic"
        >
          <option value="">All topics</option>
          {topics.map((topic) => (
            <option key={topic.id} value={topic.id}>
              {topic.name}
            </option>
          ))}
        </select>

        <select
          value={trackFilter}
          onChange={(e) => setTrackFilter(e.target.value)}
          className="bg-muted border-border text-foreground rounded-lg border px-2.5 py-1.5 text-sm"
          aria-label="Filter by track"
        >
          <option value="">Baseline + Adjustment</option>
          <option value="baseline">Baseline track only</option>
          <option value="adjustment">Adjustments only</option>
        </select>
      </div>

      {showHistory && (
        <p className="text-muted-foreground flex items-center gap-1.5 text-xs">
          <CheckCircle2 size={13} />
          History shows every recommendation and its current lifecycle state —
          assigned, postponed, or dismissed — so the coach can always see why a
          problem is or is not in the active list.
        </p>
      )}

      {/* List */}
      {visible.length === 0 ? (
        <div className="text-muted-foreground border-border rounded-lg border border-dashed py-10 text-center text-sm">
          No recommendations match the current filters.
        </div>
      ) : (
        <div className="space-y-2">
          {visible.map((rec) => (
            <RecommendationCard
              key={rec.id}
              rec={rec}
              onStatusChange={setRecommendationStatus}
              statusDiff={pendingDiff.find(
                (item) => item.key === `recommendation-status:${rec.id}`,
              )}
            />
          ))}
        </div>
      )}
    </div>
  )
}
